import _head from 'lodash/head';
import get from 'lodash/get';
import RestClient from '../RestClient/RestClient';
import ContentApiConstants from './ContentApiConstants';
import * as OrderApiConstants from '../Order/OrderApiConstants';
import { apiHosts } from '../RestClient/FetchHelper.config';
import {
    getIrisConfiguration,
    extractUiContent,
    getChannel,
    extractUiContentFromCM,
} from '../Common/CommonHelper';
import { IRIS_MARKETING_SLOT_URL_WITH_SEARCH } from '../Common/CommonConstants';

const getPageIdByPageName = (pageName) => {
    let pageId;

    switch (pageName) {
        default:
            pageId = ContentApiConstants.GALLERY_PAGE_ID;
            break;

        case 'home':
            pageId = ContentApiConstants.HOME_PAGE_ID;
            break;

        case 'rewards':
            pageId = ContentApiConstants.REWARDS_PAGE_ID;
            break;

        case 'exclusions':
            pageId = ContentApiConstants.EXCLUSIONS_PAGE_ID;
            break;
    }

    return pageId;
};

const getZoneUrl = (pageName) => {
    let url;

    switch (pageName) {
        default:
            url = ContentApiConstants.ZONE_DEPARTMENT_URL;
            break;

        case 'home':
            url = ContentApiConstants.ZONE_HOME_URL;
            break;

        case 'department':
            url = ContentApiConstants.ZONE_DEPARTMENT_URL;
            break;
    }

    return url;
};

const templateURL = {
    home: ContentApiConstants.TEMPLATE_HOME_URL,
    department: ContentApiConstants.TEMPLATE_DEPARTMENT_URL,
};

/**
 * Sometime api returns all day content where expiry does not have correct value
 * Instead of looking at the contentExpiry we have to set the date to be mid night
 */
const getAllContentExpiryDate = () => {
    const defaultDate = new Date();
    defaultDate.setHours(23);
    defaultDate.setMinutes(59);
    defaultDate.setSeconds(59);
    return defaultDate;
};

/**
 * Returns TTL from cache headers
 *
 * @static
 *
 * @memberOf HomePageContentApi
 */
export const getContentExpiryInCST = (content) => content && content.contentExpiry; // When I was asked to use this attribute this was returning CST

/**
 * Function to fetch the promo banner content.
 */
// TODO: This needs to be revisited as we need to have both mobile and tablet specific.
export const getPromoBannerContent = () =>
    RestClient.get(ContentApiConstants.PROMO_BANNER_CONTENT_URL);

/*
 *Function to fetch marketing content.
 */
export const getMarketingContent = () => RestClient.get(ContentApiConstants.MARKETING_CONTENT_URL);

/*
 * Function to fetch Marketing slots content for mobile.
 */
export const getMarketingSlotsContent = ({ deviceType, pageName }) => {
    const pageId = getPageIdByPageName(pageName);
    const url = `${ContentApiConstants.MARKETING_SLOTS_CONTENT_URL}/${pageId}/${deviceType}`;

    return RestClient.get(url).then((json) => {
        // Massaging Data for the API
        const carouselData = json.data.slots.filter((item) => /^C/.test(item.name));
        const bannerData = json.data.slots.filter((item) => /^S/.test(item.name));

        return {
            carouselData,
            bannerData,
        };
    });
};

/**
 * Function to fetch the zones
 */

/* API to load page structure returning zones and SEO
 *
 * @static
 * @param {any} pageName
 * @param {any} nId
 * @returns
 *
 * @memberOf HomePageContentApi
 */
export const getPageTemplate = (pageName, nId) => {
    const newNid = nId && nId.length ? `/${nId}` : '';
    const url = `${templateURL[pageName]}${newNid}`;

    const config = {
        apiHost: apiHosts.appHost,
        url,
        version: '/v5',
    };

    return RestClient.get(config).then((json) => ({
        data: json.data,
    }));
};

/**
 * Function to fetch the zones
 */

export const getZoneContent = (pageName, zoneName, nId = '') => {
    // TODO: dpAkamaiOverride should be removed once we got Apigee endpoints
    const newNid = nId.length ? `/${nId}` : '';
    const url = `${getZoneUrl(pageName)}${newNid}?op=${zoneName}`;
    const config = {
        url,
        apiHost: apiHosts.appHost,
        version: '/v5',
    };

    return RestClient.get(config).then((json) => {
        /* istanbul ignore else */
        if (pageName === 'home') {
            const content = _head(json.data.homePageContents);
            /* istanbul ignore else */
            if (content) {
                /* istanbul ignore else */
                if (content.allDay === true) {
                    content.ttl = getAllContentExpiryDate();
                } else {
                    content.ttl = getContentExpiryInCST(content);
                }
                return content;
            }
        }

        return json.data;
    });
};

/**
 *
 * This API orchestrates all the call required to build the home page
 * @static
 * @param {any} pageName
 * @param {any} nId
 * @returns
 *
 * @memberOf HomePageContentApi
 */
export const getPageAsContent = (pageName, nId) => {
    const pageTemplate = getPageTemplate(pageName, nId);

    /**
     * If anything goes wrong resolve with error
     * @param {*} error
     * @param {*} resolve
     */
    const templateLoadError = (error, resolve) => {
        resolve({
            status: 500,
            error,
        });
    };

    /**
     * Load all zone using the give template
     * Iterates through the page template and issues api call for each zone
     * On success or error, the zone content is returned as array
     * @param {*} template
     * @param {*} resolve
     */
    const loadAllZones = (loadedTemplate, resolve) => {
        const template = loadedTemplate.data;
        /* istanbul ignore else */
        if (!template) {
            templateLoadError(new Error('Template not found. Returned empty list'), resolve);
        }
        const zones = (template.homePageContents || []).map((zone) =>
            getZoneContent(pageName, zone, nId)
        );
        const ttls = []; // Main template never gave content expiry. Assumption was akamai cache would be flushed if there are any intraday zone orderign change

        return Promise.all(zones).then((zoneContents) => {
            const composeZoneData = {};
            const response = {};
            zoneContents.forEach((zoneContent) => {
                /* istanbul ignore else */
                if (zoneContent && zoneContent.type) {
                    // If API failed we would like gracefully handle and let client side retry if it was rendered server
                    composeZoneData[zoneContent.type] = zoneContent;
                    if (zoneContent.allDay === true) {
                        ttls.push(getAllContentExpiryDate());
                    } else {
                        ttls.push(zoneContent.ttl);
                    }
                }
            });
            response[pageName] = composeZoneData;
            const zoneResponse = {
                pageTemplate: template,
                ttls,
                content: response,
            };
            resolve(zoneResponse);
        });
    };

    const promise = new Promise((resolve) => {
        // Retrive the page template
        pageTemplate
            .then((
                template // On success load all child content
            ) => loadAllZones(template, resolve))
            .catch((error) => templateLoadError(error, resolve));
    });
    return promise;
};

/**
 * Function to fetch the message content from IRIS - content aggregator
 * This is function is used to fetch data/html from IRIS directly
 * no bcc fallback
 */
export const getMessageContentNew = (params, supportMultiZoneData = false) => {
    const config = getIrisConfiguration();
    const searchTerm = get(params, 'payload.searchTerm') || '';
    config.url = IRIS_MARKETING_SLOT_URL_WITH_SEARCH.replace(/{searchTerm}/gi, searchTerm);
    if (get(params, 'payload.isChannelRequired')) {
        const context = RestClient.getContext();
        config.channel = getChannel(context);
    }
    if (get(params, 'payload.isXCloudHeaderRequired')) {
        config.isXCloudHeaderRequired = true;
    }
    const { isCMHeaderRequired = false } = params;
    config.isCMHeaderRequired = isCMHeaderRequired;
    return RestClient.get(config).then((json) => {
        if (!get(params, 'payload.skipExtractUIContent')) {
            const irisContent = isCMHeaderRequired
                ? extractUiContentFromCM(json, supportMultiZoneData)
                : extractUiContent(json);
            return {
                id: searchTerm,
                title: irisContent.title,
                body: irisContent.html,
                status: json.status,
            };
        }
        return get(json, 'data.content.components', []);
    });
};

/* istanbul ignore next */
export const getMessageContentSupportingCM = (params, supportMultiZoneData = false) => {
    const config = getIrisConfiguration();
    const searchTerm = get(params, 'payload.searchTerm') || '';
    config.url = IRIS_MARKETING_SLOT_URL_WITH_SEARCH.replace(/{searchTerm}/gi, searchTerm);
    if (get(params, 'payload.isChannelRequired')) {
        const context = RestClient.getContext();
        config.channel = getChannel(context);
    }
    if (get(params, 'payload.isXCloudHeaderRequired')) {
        config.isXCloudHeaderRequired = true;
    }
    const { isCMHeaderRequired = false } = params;
    config.isCMHeaderRequired = isCMHeaderRequired;
    return RestClient.get(config).then((json) => {
        return isCMHeaderRequired
            ? {
                  status: json.status,
                  content: get(json, 'data.page.grid.placements[0].items', []),
              }
            : get(json, 'data.content.components', []);
    });
};

/* istanbul ignore next */
export const getMessageContentCM = (params) => {
    const config = getIrisConfiguration();
    const searchTerm = get(params, 'payload.searchTerm') || '';
    config.url = IRIS_MARKETING_SLOT_URL_WITH_SEARCH.replace(/{searchTerm}/gi, searchTerm);
    if (get(params, 'payload.isChannelRequired')) {
        const context = RestClient.getContext();
        config.channel = getChannel(context);
    }
    if (get(params, 'payload.isXCloudHeaderRequired')) {
        config.isXCloudHeaderRequired = true;
    }
    const { isCMHeaderRequired = false } = params;
    config.isCMHeaderRequired = isCMHeaderRequired;
    return RestClient.get(config).then((json) => {
        return isCMHeaderRequired
            ? {
                  status: json.status,
                  content: get(json, 'data', []),
              }
            : get(json, 'data.content.components', []);
    });
};

/**
 * Function to fetch the message content.
 */
export const getMessageContent = (params, supportMultiZoneData = false) => {
    let config = {};
    const { payload } = params;
    config = getIrisConfiguration();
    config.isCMHeaderRequired = params.isCMHeaderRequired || false;
    config.url = OrderApiConstants.IRIS_CONTENT_AGGREGATOR_URL.replace(
        /@irisSearchTerm@/g,
        payload
    );
    return RestClient.get(config).then((json) => {
        const irisContent = params.isCMHeaderRequired
            ? extractUiContentFromCM(json, supportMultiZoneData)
            : extractUiContent(json, supportMultiZoneData);
        return {
            id: params.payload,
            title: irisContent.title,
            body: irisContent.html,
            status: json.status,
        };
    });
};

/*
 * Function to fetch Marketing slots content for mobile.
 */

export const getRewardsContent = ({ deviceType, pageName }) => {
    const pageId = getPageIdByPageName(pageName);
    const url = `${ContentApiConstants.REWARDS_CONTENT_URL}/${pageId}/${deviceType}/S1`;

    return RestClient.get(url).then((json) => json);
};

/*
 * Function to fetch Exclusion slots content for mobile.
 */
export const getExclusionContent = () => {
    const url = `${ContentApiConstants.EXCLUSIONS_CONTENT_URL}/${ContentApiConstants.EXCLUSIONS_PAGE_ID}/mobile/S1`;

    return RestClient.get(url).then((json) => json);
};
